import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";

import AdminProfile from "../user/AdminProfile";
import { BLOG_CATEGORIES } from "../../constants/constants";
import { Helmet } from "react-helmet-async";
import {
  useGetBlogDetailsQuery,
  useUpdateBlogMutation,
} from "../../redux/api/blogsApi";
import Loader from "../layout/Loader";

const UpdateBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const quillRef = useRef(null);

  const [blog, setBlog] = useState({
    name: "",
    description: "",
    category: BLOG_CATEGORIES[0] || "",
    author: "",
  });

  const { name, description, category, author } = blog;

  const [updateBlog, { isLoading, error, isSuccess }] = useUpdateBlogMutation();
  const { data } = useGetBlogDetailsQuery(id);

  const [isPopulated, setIsPopulated] = useState(false);

  useEffect(() => {
    if (data?.blog && !isPopulated) {
      setBlog({
        name: data.blog.name || "",
        description: data.blog.description || "",
        category: data.blog.category || BLOG_CATEGORIES[0],
        author: data.blog.author || "",
      });
      setIsPopulated(true);
    }

    if (error) toast.error(error?.data?.message);

    if (isSuccess) {
      toast.success("Blog updated successfully!");
      navigate("/admin/blogs");
    }
  }, [error, navigate, data, isSuccess, isPopulated]);

  const onChange = (e) => {
    setBlog({ ...blog, [e.target.name]: e.target.value });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    updateBlog({ id, body: blog });
  };

  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      const loadingToast = toast.loading("Uploading image...");

      try {
        const reader = new FileReader();

        reader.onload = async () => {
          try {
            const base64Image = reader.result;
            const { data } = await axios.post(
              "/api/v1/upload/editor-image",
              { image: base64Image },
              {
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
              }
            );

            if (data.success) {
              const quill = quillRef.current.getEditor();
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, "image", data.url);
              quill.setSelection(range.index + 1);
              toast.success("Image uploaded successfully", { id: loadingToast });
            } else {
              toast.error("Upload failed", { id: loadingToast });
            }
          } catch (err) {
            toast.error(
              err.response?.data?.message || "Image upload failed",
              { id: loadingToast }
            );
          }
        };

        reader.onerror = () => {
          toast.error("Failed to read image", { id: loadingToast });
        };

        reader.readAsDataURL(file);
      } catch (err) {
        toast.error("An unexpected error occurred", { id: loadingToast });
      }
    };
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
    }),
    []
  );

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "blockquote", "list", "bullet", "link", "image", "video",
  ];

  if (!isPopulated) return <Loader />;

  return (
    <AdminProfile>
      <Helmet>
        <title>Update Blog</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full mt-10 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-playfair text-stone-900 mb-6">
          Update Blog
        </h1>

        <form
          onSubmit={submitHandler}
          className="bg-white border border-stone-200 shadow rounded-lg p-6 space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-stone-800 mb-1">
                Blog Title
              </label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={onChange}
                required
                className="w-full px-4 py-2 border border-stone-300 rounded-md focus:border-[#BC3F4A] focus:ring-[#BC3F4A] focus:outline-none text-sm"
                placeholder="Enter blog title"
              />
            </div>

            {/* Category */}
            <div className="flex items-end">
              <label className="sr-only" htmlFor="category_field">
                Category
              </label>
              <select
                id="category_field"
                name="category"
                value={category}
                onChange={onChange}
                className="w-full px-4 py-2 border border-stone-300 rounded-md focus:border-[#BC3F4A] focus:ring-[#BC3F4A] focus:outline-none text-sm"
              >
                {BLOG_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Author */}
            <div>
              <label className="block text-sm font-medium text-stone-800 mb-1">
                Author
              </label>
              <input
                type="text"
                name="author"
                value={author}
                onChange={onChange}
                required
                className="w-full px-4 py-2 border border-stone-300 rounded-md focus:border-[#BC3F4A] focus:ring-[#BC3F4A] focus:outline-none text-sm"
                placeholder="Author name"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-stone-800 mb-1">
              Main Content
            </label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={description}
              onChange={(value) => setBlog({ ...blog, description: value })}
              placeholder="Write your blog post here..."
              modules={modules}
              formats={formats}
              className="border border-stone-300 rounded-md h-auto"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/blogs")}
              className="w-full py-3 px-4 rounded-md border border-stone-300 text-stone-700 font-playfair hover:bg-stone-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 rounded-md bg-[#BC3F4A] text-white font-playfair hover:bg-[#a3323b] transition disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Update Blog"}
            </button>
          </div>
        </form>
      </div>
    </AdminProfile>
  );
};

export default UpdateBlog;